import Alpine from "alpinejs";
import * as uiUtils from "../../editor/v2/utils/uiUtils";

Alpine.data("navigationFE", () => ({
    isHovered: false,
    logoStyles: '',
    linksHolderStyles: '',
    linksNormalStyles: '',
    linksParentNormalStyles: '',
    linksParentHoverStyles: '',
    activeNavNormal: '',
    activeLinkParentNormalStyles: '',
    linksHoverStyles: '',
    activeLinkParentHoverStyles: '',

    styles: '',

    buttons: [],
    navOverlap : false,

    init(styles, buttons) {
        this.styles = styles;

        if (this.styles) {
            this.refreshNavigationStyles();
        }
    },

    getBorderWidth(backgroundType, side, type) {
        // Safe access: check if styles[type] and border exist
        const borderConfig = this.styles[type]?.border?.[backgroundType];

        if (borderConfig && (borderConfig.selectedPosition === 'all' || borderConfig.selectedPosition === side)) {
            return borderConfig.borderWidth + borderConfig.borderWidthUnit;
        } else {
            return '0px'
        }
    },

    getBoxShadow(backgroundType, selectedDevice) {
        return `box-shadow: ${(this.styles.dropdowns.shadow[backgroundType][selectedDevice].shadowType.indexOf('inset') > -1) ? 'inset' : ''} ${this.styles.dropdowns.shadow[backgroundType][selectedDevice].offsetX}px ${this.styles.dropdowns.shadow[backgroundType][selectedDevice].offsetY}px ${this.styles.dropdowns.shadow[backgroundType][selectedDevice].blurRadius}px ${this.styles.dropdowns.shadow[backgroundType][selectedDevice].spreadRadius}px rgba(${this.hexToRgb(this.styles.dropdowns.shadow[backgroundType][selectedDevice].shadowColor)}, ${this.styles.dropdowns.shadow[backgroundType][selectedDevice].opacity / 100})`;
    },

    hexToRgb(hex) {
        if (hex === null || !hex) return ``;

        let r = 0, g = 0, b = 0;
        if (hex.length === 4) {
            r = parseInt(hex[1] + hex[1], 16);
            g = parseInt(hex[2] + hex[2], 16);
            b = parseInt(hex[3] + hex[3], 16);
        }
        // 6 digits
        else if (hex.length === 7) {
            r = parseInt(hex[1] + hex[2], 16);
            g = parseInt(hex[3] + hex[4], 16);
            b = parseInt(hex[5] + hex[6], 16);
        }
        return `${r}, ${g}, ${b}`;
    },

    getButtonHoverStyles(button) {
        const buttonStyles = button.styles;
        let styles = '';

        if (buttonStyles.textColor.hover || buttonStyles.textColor.normal) {
            styles = 'color: ' + (buttonStyles.textColor.hover || buttonStyles.textColor.normal);
        }
        if (buttonStyles.backgroundColor.hover || buttonStyles.backgroundColor.normal) {
            styles += '; background: ' + (buttonStyles.backgroundColor.hover || buttonStyles.backgroundColor.normal);
        }
        if (buttonStyles.borderWidth.hover || buttonStyles.borderWidth.normal) {
            styles += '; border: ' + (buttonStyles.borderWidth.hover || buttonStyles.borderWidth.normal) + 'px solid';
        }
        if (buttonStyles.borderColor.hover || buttonStyles.borderColor.normal) {
            styles += ' ' + (buttonStyles.borderColor.hover || buttonStyles.borderColor.normal) + ';';
        }

        return this.getGlobalButtonStyles(styles);
    },

    getGlobalButtonStyles(styles) {
        // 1. Safety Check: Ensure buttons config exists
        const buttonsConfig = this.styles?.buttons || {};
        const textSizeConfig = buttonsConfig.textSize || {};
        const fontsConfig = buttonsConfig.fontsAndTypography || {};
        const radiusConfig = buttonsConfig.borderRadius;
        const paddingConfig = buttonsConfig.padding;
        const gapConfig = buttonsConfig.iconAndTextGap;

        // 2. Safe Value Extraction
        const desktopSize = textSizeConfig.desktop ? textSizeConfig.desktop + 'px' : '';
        const tabletSize = textSizeConfig.tablet ? textSizeConfig.tablet + 'px' : desktopSize;
        const mobileSize = textSizeConfig.mobile ? textSizeConfig.mobile + 'px' : tabletSize;

        // 3. Apply Responsive Font Size Safely
        let activeFontSize = '';
        if (window.matchMedia('(max-width: 767px)').matches) {
            activeFontSize = mobileSize;
        } else if (window.matchMedia('(min-width: 768px) and (max-width: 1024px)').matches) {
            activeFontSize = tabletSize;
        } else {
            activeFontSize = desktopSize;
        }

        // Only append if a size was actually found
        if (activeFontSize) {
            styles += '; font-size: ' + activeFontSize;
        }

        // 4. Apply Remaining Styles Safely

        // Safely apply font weight if it exists
        if (fontsConfig.weight) {
            styles += "; font-weight: " + fontsConfig.weight;
        }

        // Safely check and apply border radius
        if (radiusConfig) {
            // Fallback to 0 if a specific corner is missing
            const tl = radiusConfig.topLeft || 0;
            const tr = radiusConfig.topRight || 0;
            const br = radiusConfig.bottomRight || 0;
            const bl = radiusConfig.bottomLeft || 0;
            styles += `; border-radius: ${tl}px ${tr}px ${br}px ${bl}px;`;
        }

        // Safely check and apply padding using optional chaining (?.)
        const pDesktop = paddingConfig?.desktop;
        if (pDesktop) {
            if (pDesktop.top !== undefined && pDesktop.top !== '') styles += '; padding-top: ' + pDesktop.top + 'px';
            if (pDesktop.bottom !== undefined && pDesktop.bottom !== '') styles += '; padding-bottom: ' + pDesktop.bottom + 'px';
            if (pDesktop.left !== undefined && pDesktop.left !== '') styles += '; padding-left: ' + pDesktop.left + 'px';
            if (pDesktop.right !== undefined && pDesktop.right !== '') styles += '; padding-right: ' + pDesktop.right + 'px';
        }

        // Safely apply gap
        if (gapConfig !== undefined && gapConfig !== '') {
            styles += '; gap: ' + gapConfig + 'px';
        }

        return styles;
    },

    refreshNavigationStyles() {
        if (!this.styles) return;

        // --- 1. SAFELY EXTRACT CONFIGS (Add empty object defaults) ---
        const linksStyleConfig = this.styles.links || {};
        const activeLinkStyleConfig = this.styles.activeLink || {};
        const dropdownsStyle = this.styles.dropdowns || {};
        const logoStyle = this.styles.logo || { size: { desktop: {}, tablet: {}, mobile: {} } };
        const searchBoxStyle = this.styles.search || { width: {}, height: {}, borderRadius: {} };

        // --- 2. SAFELY EXTRACT SUB-CONFIGS ---
        const textSizeConfig = linksStyleConfig.textSize || {};
        const textColorConfig = linksStyleConfig.color || {};
        const letterSpacingConfig = linksStyleConfig.letterSpacing || {};
        const borderConfig = linksStyleConfig.border || { normal: {}, hover: {} };
        const activeBorderConfig = activeLinkStyleConfig.border || { normal: {}, hover: {} };

        const desktopSize = textSizeConfig.desktop ? textSizeConfig.desktop + 'px' : ''
        const tabletSize = textSizeConfig.tablet ? textSizeConfig.tablet + 'px' : desktopSize;
        const mobileSize = textSizeConfig.mobile ? textSizeConfig.mobile + 'px' : tabletSize;

        const desktopSpacing = letterSpacingConfig.desktop ? letterSpacingConfig.desktop + 'px' : '';
        const tabletSpacing = letterSpacingConfig.tablet ? letterSpacingConfig.tablet + 'px' : desktopSpacing;
        const mobileSpacing = letterSpacingConfig.mobile ? letterSpacingConfig.mobile + 'px' : tabletSpacing;

        //dropdowns
        const dropdownTextSizeConfig = dropdownsStyle.textSize || {};
        const dropdownLetterSpacingConfig = dropdownsStyle.letterSpacing || {};
        const dropdownWidthConfig = dropdownsStyle.width || {};

        const dropdownDesktopSize = dropdownTextSizeConfig.desktop ? dropdownTextSizeConfig.desktop + 'px' : '';
        const dropdownTabletSize = dropdownTextSizeConfig.tablet ? dropdownTextSizeConfig.tablet + 'px' : dropdownDesktopSize;
        const dropdownMobileSize = dropdownTextSizeConfig.mobile ? dropdownTextSizeConfig.mobile + 'px' : dropdownTabletSize;

        const dropdownDesktopSpacing = dropdownLetterSpacingConfig.desktop ? dropdownLetterSpacingConfig.desktop + 'px' : '';
        const dropdownTabletSpacing = dropdownLetterSpacingConfig.tablet ? dropdownLetterSpacingConfig.tablet + 'px' : dropdownDesktopSpacing;
        const dropdownMobileSpacing = dropdownLetterSpacingConfig.mobile ? dropdownLetterSpacingConfig.mobile + 'px' : dropdownTabletSpacing;

        const dropdownDesktopWidth = dropdownWidthConfig.desktop ? dropdownWidthConfig.desktop + 'px' : '';
        const dropdownTabletWidth = dropdownWidthConfig.tablet ? dropdownWidthConfig.tablet + 'px' : dropdownDesktopWidth;
        const dropdownMobileWidth = dropdownWidthConfig.mobile ? dropdownWidthConfig.mobile + 'px' : dropdownTabletWidth;

        // Safely access logo size using optional chaining (?.)
        const logoWidthDesktop = logoStyle.size?.desktop?.width ? logoStyle.size.desktop.width + 'px' : '';
        const logoWidthTablet = logoStyle.size?.tablet?.width ? logoStyle.size.tablet.width + 'px' : logoWidthDesktop;
        const logoWidthMobile = logoStyle.size?.mobile?.width ? logoStyle.size.mobile.width + 'px' : logoWidthTablet;

        const logoHeightDesktop = logoStyle.size?.desktop?.height ? logoStyle.size.desktop.height + 'px' : '';
        const logoHeightTablet = logoStyle.size?.tablet?.height ? logoStyle.size.tablet.height + 'px' : logoHeightDesktop;
        const logoHeightMobile = logoStyle.size?.mobile?.height ? logoStyle.size.mobile.height + 'px' : logoHeightTablet;

        //search box
        const searchBoxWidthDesktop = searchBoxStyle.width?.desktop ? searchBoxStyle.width.desktop + 'px' : '';
        const searchBoxWidthTablet = searchBoxStyle.width?.tablet ? searchBoxStyle.width.tablet + 'px' : searchBoxWidthDesktop;
        const searchBoxWidthMobile = searchBoxStyle.width?.mobile ? searchBoxStyle.width.mobile + 'px' : searchBoxWidthTablet;

        const searchBoxHeightDesktop = searchBoxStyle.height?.desktop ? searchBoxStyle.height.desktop + 'px' : '';
        const searchBoxHeightTablet = searchBoxStyle.height?.tablet ? searchBoxStyle.height.tablet + 'px' : searchBoxHeightDesktop;
        const searchBoxHeightMobile = searchBoxStyle.height?.mobile ? searchBoxStyle.height.mobile + 'px' : searchBoxHeightTablet;

        if (window.matchMedia('(max-width: 767px)').matches) {
            this.linksNormalStyles = 'font-size: ' + mobileSize;
            this.linksHoverStyles = 'font-size: ' + mobileSize;

            this.linksNormalStyles = this.linksNormalStyles + '; letter-spacing: ' + mobileSpacing;
            this.linksHoverStyles = this.linksHoverStyles + '; letter-spacing: ' + mobileSpacing;

            this.dropdownLinksNormalStyles = 'font-size: ' + dropdownMobileSize;
            this.dropdownLinksHoverStyles = 'font-size: ' + dropdownMobileSize;

            this.dropdownLinksNormalStyles = this.dropdownLinksNormalStyles + '; letter-spacing: ' + dropdownMobileSpacing;
            this.dropdownLinksHoverStyles = this.dropdownLinksHoverStyles + '; letter-spacing: ' + dropdownMobileSpacing;

            this.dropdownsStyles = 'width: ' + dropdownMobileWidth;

            this.searchStyle = 'width: ' + searchBoxWidthMobile;
            this.searchStyle = this.searchStyle + '; height: ' + searchBoxHeightMobile;

            if (logoStyle.size?.mobile?.aspectRatio) {
                this.logoStyles = 'width: auto; height: ' + logoHeightMobile;
            } else {
                this.logoStyles = 'width: ' + logoWidthMobile + ' ; height: ' + logoHeightMobile;
            }
        } else if (window.matchMedia('(min-width: 768px) and (max-width: 1024px)').matches) {
            this.linksNormalStyles = 'font-size: ' + tabletSize;
            this.linksHoverStyles = 'font-size: ' + tabletSize;

            this.linksNormalStyles = this.linksNormalStyles + '; letter-spacing: ' + tabletSpacing;
            this.linksHoverStyles = this.linksHoverStyles + '; letter-spacing: ' + tabletSpacing;

            this.dropdownLinksNormalStyles = 'font-size: ' + dropdownTabletSize;
            this.dropdownLinksHoverStyles = 'font-size: ' + dropdownTabletSize;

            this.dropdownLinksNormalStyles = this.dropdownLinksNormalStyles + '; letter-spacing: ' + dropdownTabletSpacing;
            this.dropdownLinksHoverStyles = this.dropdownLinksHoverStyles + '; letter-spacing: ' + dropdownTabletSpacing;

            this.dropdownsStyles = 'width: ' + dropdownTabletWidth;

            this.searchStyle = 'width: ' + searchBoxWidthTablet;
            this.searchStyle = this.searchStyle + '; height: ' + searchBoxHeightTablet;

            if (logoStyle.size?.tablet?.aspectRatio) {
                this.logoStyles = 'width: auto; height: ' + logoHeightTablet;
            } else {
                this.logoStyles = 'width: ' + logoWidthTablet + ' ; height: ' + logoHeightTablet;
            }
        } else {
            this.linksNormalStyles = 'font-size: ' + desktopSize;
            this.linksHoverStyles = 'font-size: ' + desktopSize;

            this.linksNormalStyles = this.linksNormalStyles + '; letter-spacing: ' + desktopSpacing;
            this.linksHoverStyles = this.linksHoverStyles + '; letter-spacing: ' + desktopSpacing;

            this.dropdownLinksNormalStyles = 'font-size: ' + dropdownDesktopSize;
            this.dropdownLinksHoverStyles = 'font-size: ' + dropdownDesktopSize;

            this.dropdownLinksNormalStyles = this.dropdownLinksNormalStyles + '; letter-spacing: ' + dropdownDesktopSpacing;
            this.dropdownLinksHoverStyles = this.dropdownLinksHoverStyles + '; letter-spacing: ' + dropdownDesktopSpacing;

            this.dropdownsStyles = 'width: ' + dropdownDesktopWidth;

            this.searchStyle = 'width: ' + searchBoxWidthDesktop;
            this.searchStyle = this.searchStyle + '; height: ' + searchBoxHeightDesktop;

            if (logoStyle.size?.desktop?.aspectRatio) {
                this.logoStyles = 'width: auto; height: ' + logoHeightDesktop;
            } else {
                this.logoStyles = 'width: ' + logoWidthDesktop + ' ; height: ' + logoHeightDesktop;
            }
        }

        this.linksNormalStyles = this.linksNormalStyles +  '; color: ' + (textColorConfig.normal || '');
        this.linksHoverStyles = this.linksHoverStyles +  '; color: ' + (textColorConfig.hover || '');

        // Safely access border configs using checks or optional chaining
        this.linksParentNormalStyles =
            ' border-top: ' + this.getBorderWidth('normal', 'top', 'links') +
            '; border-bottom: ' + this.getBorderWidth('normal', 'bottom', 'links') +
            '; border-left: ' + this.getBorderWidth('normal', 'left', 'links') +
            '; border-right: ' + this.getBorderWidth('normal', 'right', 'links') +
            '; border-style: ' + (borderConfig.normal?.borderStyle || 'none') +
            '; border-color: ' + (borderConfig.normal?.color || 'transparent');

        this.linksParentHoverStyles =
            ' border-top: ' + this.getBorderWidth('hover', 'top', 'links') +
            '; border-bottom: ' + this.getBorderWidth('hover', 'bottom', 'links') +
            '; border-left: ' + this.getBorderWidth('hover', 'left', 'links') +
            '; border-right: ' + this.getBorderWidth('hover', 'right', 'links') +
            '; border-style: ' + (borderConfig.hover?.borderStyle || 'none') +
            '; border-color: ' + (borderConfig.hover?.color || 'transparent');

        this.activeLinkParentNormalStyles =
            ' border-top: ' + this.getBorderWidth('normal', 'top', 'activeLink') +
            '; border-bottom: ' + this.getBorderWidth('normal', 'bottom', 'activeLink') +
            '; border-left: ' + this.getBorderWidth('normal', 'left', 'activeLink') +
            '; border-right: ' + this.getBorderWidth('normal', 'right', 'activeLink') +
            '; border-style: ' + (activeBorderConfig.normal?.borderStyle || 'none') +
            '; border-color: ' + (activeBorderConfig.normal?.color || 'transparent');

        this.activeLinkParentHoverStyles =
            ' border-top: ' + this.getBorderWidth('hover', 'top', 'activeLink') +
            '; border-bottom: ' + this.getBorderWidth('hover', 'bottom', 'activeLink') +
            '; border-left: ' + this.getBorderWidth('hover', 'left', 'activeLink') +
            '; border-right: ' + this.getBorderWidth('hover', 'right', 'activeLink') +
            '; border-style: ' + (activeBorderConfig.hover?.borderStyle || 'none') +
            '; border-color: ' + (activeBorderConfig.hover?.color || 'transparent');

        this.linksHolderStyles = 'gap: ' + (linksStyleConfig.gapBetween) + 'px';

        const activeNavNormalColor = activeLinkStyleConfig.color?.normal ? activeLinkStyleConfig.color.normal : (textColorConfig.normal || '');
        const activeNavHoverColor = activeLinkStyleConfig.color?.hover ? activeLinkStyleConfig.color.hover : (textColorConfig.hover || '');

        this.activeNavNormal = this.linksNormalStyles + '; color: ' + activeNavNormalColor;
        this.activeNavHover = this.linksHoverStyles + '; color: ' + activeNavHoverColor;

        if (dropdownsStyle.backgroundStyle?.normal?.backgroundType === 'color') {
            this.dropdownsStyles = this.dropdownsStyles + '; background: ' + dropdownsStyle.backgroundStyle.normal.color;
        } else if (dropdownsStyle.backgroundStyle) {
            this.dropdownsStyles = this.dropdownsStyles + '; ' + uiUtils.previewNormalStyle(dropdownsStyle.backgroundStyle);
        }

        if (dropdownsStyle.padding?.desktop) {
            this.dropdownsStyles = this.dropdownsStyles + '; padding-top: ' + dropdownsStyle.padding.desktop.top + 'px';
            this.dropdownsStyles = this.dropdownsStyles + '; padding-bottom: ' + dropdownsStyle.padding.desktop.bottom + 'px';
            this.dropdownsStyles = this.dropdownsStyles + '; padding-left: ' + dropdownsStyle.padding.desktop.left + 'px';
            this.dropdownsStyles = this.dropdownsStyles + '; padding-right: ' + dropdownsStyle.padding.desktop.right + 'px';
        }

        if (dropdownsStyle.borderRadius) {
            this.dropdownsStyles = this.dropdownsStyles + `; border-radius: ${dropdownsStyle.borderRadius.topLeft}px ${dropdownsStyle.borderRadius.topRight}px ${dropdownsStyle.borderRadius.bottomRight}px ${dropdownsStyle.borderRadius.bottomLeft}px;`
        }

        if (dropdownsStyle.shadow) {
            this.dropdownsStyles = this.dropdownsStyles + ' ' + this.getBoxShadow('normal', 'desktop');
        }

        this.dropdownLinksNormalStyles = this.dropdownLinksNormalStyles + '; color: ' + (dropdownsStyle.color?.normal || '');
        this.dropdownLinksHoverStyles = this.dropdownLinksHoverStyles + '; color: ' + (dropdownsStyle.color?.hover || '');

        if (searchBoxStyle.borderRadius) {
            this.searchStyle = this.searchStyle+ `; border-radius: ${searchBoxStyle.borderRadius.topLeft}px ${searchBoxStyle.borderRadius.topRight}px ${searchBoxStyle.borderRadius.bottomRight}px ${searchBoxStyle.borderRadius.bottomLeft}px`;
        }

        this.searchStyle = this.searchStyle + '; background-color: ' + (searchBoxStyle.backgroundColor || '');
        this.searchStyle = this.searchStyle + '; outline-color: ' + (searchBoxStyle.outlineColor || '');

        this.navOverlap = this.styles.general?.overlapNavigation;
    },

    getButtonStyles(button) {
        console.log(button);
        const buttonStyles = button.styles;
        let styles = '';

        if (buttonStyles.textColor && buttonStyles.textColor.normal) {
            styles = 'color: ' + buttonStyles.textColor.normal;
        }
        if (buttonStyles.backgroundColor && buttonStyles.backgroundColor.normal) {
            styles = styles + '; background: ' + buttonStyles.backgroundColor.normal;
        }
        if (buttonStyles.borderWidth && buttonStyles.borderWidth.normal) {
            styles = styles + '; border: ' + buttonStyles.borderWidth.normal + 'px solid';
        }
        if (buttonStyles.borderColor && buttonStyles.borderColor.normal) {
            styles = styles + buttonStyles.borderColor.normal + ';'
        }
        return this.getGlobalButtonStyles(styles);
    }
}));
